import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Typography, Popover, Tooltip } from '@mui/material'
import { observer } from 'mobx-react-lite'
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded'
import PersonRoundedIcon from '@mui/icons-material/PersonRounded'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import { AppPaper } from 'src/component/common/analog/molecule/AppPaper'
import { Palette } from 'src/feature/theme/model/Palette'
import { appRoutePath } from 'src/feature/router/util/app-route-path'
import { UsedSkillMetrics } from '../../model/my-team-skill-gaps'
import { useMyTeamStore } from '../../store/my-team.store'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { getColorByRating } from '../../util/skillGapUtils'

const COUNT_WIDTH = 12
const ICON_SIZE = 14

export interface TeamUnusedSkillGapBoxProps {
  skillSet: UsedSkillMetrics
}

/**
 * Displays unused skill with user count icon and rating distribution counts.
 * Clicking on the person icon shows a popover with the list of users and their ratings.
 * User ratings are color-coded using the shared skill gap rating utility.
 */

export const TeamUnusedSkillGapBox = observer(({ skillSet }: TeamUnusedSkillGapBoxProps) => {
  const myTeamStore = useMyTeamStore()
  const { translation } = useLocalizationStore()
  const pageTranslation = translation.pages.myTeam
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  useEffect(() => {
    myTeamStore.getUnusedSkillGaps(skillSet.setName, skillSet.ids)
  }, [skillSet.setName, skillSet.ids, myTeamStore])

  const handleIconClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [])

  const handleUserClick = useCallback((userId: string, userName?: string) => {
    navigate(appRoutePath.MyTeamUserDetails(userId), {
      state: {
        initialUserMeta: {
          userId,
          name: userName || '',
          email: '',
          avatarUrl: '',
          memberSince: '',
        },
      },
    })
  }, [navigate])

  const skillGaps = myTeamStore.unusedSkillGapsOverview?.[skillSet.setName]
  const users = skillGaps?.users || []

  const countItems = useMemo(() => [
    { color: Palette.DANGER, count: skillGaps?.under2 || 0, icon: <ArrowDownwardRoundedIcon sx={{ fontSize: ICON_SIZE }} /> },
    { color: Palette.GOLDEN_ORANGE, count: skillGaps?.between2And4 || 0, icon: <CheckRoundedIcon sx={{ fontSize: ICON_SIZE }} /> },
    { color: Palette.OLIVE_GREEN, count: skillGaps?.above4 || 0, icon: <ArrowUpwardRoundedIcon sx={{ fontSize: ICON_SIZE }} /> },
  ], [skillGaps])

  return (
    <AppPaper sx={{ ...styles.root, backgroundColor: 'background.dark' }}>
      <AppBox sx={styles.container}>
        <AppBox sx={styles.skillName}>
          <Typography fontSize={12} noWrap title={skillSet.setName}>
            {skillSet.setName}
          </Typography>
        </AppBox>
        <AppBox sx={styles.rightSection}>
          <Tooltip title={pageTranslation.unusedSkillsBox.clickToSeeUserDetails()} arrow>
            <AppBox
              onClick={handleIconClick}
              sx={styles.personIconWrapper}
            >
              <PersonRoundedIcon sx={{ fontSize: 14, cursor: 'pointer' }} />
              <Typography sx={styles.countText}>{users.length}</Typography>
            </AppBox>
          </Tooltip>
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={handleClose}
            disableRestoreFocus
            slotProps={{
              paper: {
                sx: {
                  borderRadius: '10px',
                  border: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'background.paper',
                  mt: 0.5,
                },
              },
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
          >
            <AppBox sx={styles.popoverContent}>
              {users.map(user => (
                <AppBox
                  key={user.userId}
                  sx={styles.userItem}
                  onClick={() => handleUserClick(user.userId, user.userName)}
                >
                  <AppBox sx={styles.userInfoWrapper}>
                    <AppBox sx={styles.userNameRow}>
                      <AppBox sx={{ ...styles.userRatingDot, backgroundColor: getColorByRating(user.rating) }} />
                      <Typography sx={styles.userName}>{user.userName}</Typography>
                    </AppBox>
                    <Typography sx={styles.userDetail}>
                      {pageTranslation.donutChart.current()}: {user.rating.toFixed(2)} / {pageTranslation.unusedSkillsBox.maximum()}: 5.00
                    </Typography>
                  </AppBox>
                </AppBox>
              ))}
            </AppBox>
          </Popover>
          <AppBox sx={styles.countsWrapper}>
            {countItems.map(({ color, count, icon }) => (
              <AppBox key={color} sx={styles.countItem}>
                <AppBox sx={{ ...styles.iconBox, color }}>{icon}</AppBox>
                <Typography sx={styles.countText}>{count}</Typography>
              </AppBox>
            ))}
          </AppBox>
        </AppBox>
      </AppBox>
    </AppPaper>
  )
})

const styles = {
  root: {
    width: '100%',
    flexDirection: 'column',
    padding: '2px 6px',
    borderRadius: '10px',
  },
  container: {
    width: '100%',
    flexDirection: { xs: 'column', md: 'row' },
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 5,
  },
  skillName: {
    justifyContent: { xs: 'center', md: 'flex-start' },
    alignItems: 'center',
  },
  rightSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  personIconWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    cursor: 'pointer',
  },
  popoverContent: {
    flexDirection: 'column',
    padding: '2px 4px',
    gap: 2,
    maxHeight: 300,
    overflowY: 'auto',
    maxWidth: 200,
  },
  userItem: {
    flexDirection: 'column',
    gap: 2,
    padding: '6px 8px',
    borderRadius: '10px',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: 'action.hover',
    },
  },
  userInfoWrapper: {
    flexDirection: 'column',
    gap: 2,
    width: '100%',
    cursor: 'pointer',
  },
  userNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  userRatingDot: {
    height: 8,
    width: 8,
    borderRadius: '50%',
    flexShrink: 0,
  },
  userName: {
    fontSize: 11,
    fontWeight: 500,
    color: 'text.primary',
  },
  userDetail: {
    fontSize: 10,
    color: 'text.secondary',
  },
  countsWrapper: {
    flexDirection: 'row',
    gap: 5,
    alignItems: 'center',
  },
  countItem: {
    gap: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  countText: {
    fontSize: 14,
    minWidth: COUNT_WIDTH,
    textAlign: 'center',
  },
  iconBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}